import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  useRemoveMember,
  useUninviteMembers,
  useDeclineJoinRequests,
  useApproveMembers,
  ClubQueryKeys,
} from '@/services/club'
import { MemberDisplayTab } from './useClubMembers'

interface UseMemberActionsParams {
  clubId: number
  tab: MemberDisplayTab
  /** Called with the acted-on ids once the server confirms, before the lists refetch. */
  onSettled: (userIds: number[]) => void
}

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`

/**
 * The actions the Members tab takes on a selection: remove, uninvite or decline, whichever the
 * current tab means by "remove", and approve, which only pending join requests offer. Every
 * success hands the ids to onSettled and refetches each list the rows could have been in:
 * approving moves a player to Active, so the active list and count refetch with the pending ones.
 */
export const useMemberActions = ({ clubId, tab, onSettled }: UseMemberActionsParams) => {
  const queryClient = useQueryClient()
  const { mutate: removeMember, isLoading: isRemoving } = useRemoveMember()
  const { mutate: uninviteMembers, isLoading: isUninviting } = useUninviteMembers()
  const { mutate: declineRequests, isLoading: isDeclining } = useDeclineJoinRequests()
  const { mutate: approveMembers, isLoading: isApproving } = useApproveMembers()

  /** Hands the ids back to the table, then refetches every member list and tab count. */
  const settle = (userIds: number[]) => {
    onSettled(userIds)
    const keys = [
      [ClubQueryKeys.GET_CLUB_MEMBERS_BY_STATE, clubId],
      [ClubQueryKeys.GET_CLUB_MEMBERS_MANAGEMENT],
      [ClubQueryKeys.GET_CLUB_INVITES],
    ]
    keys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey, refetchType: 'all' }))
  }

  const removalLabel = tab === 'ACTIVE' ? 'Remove' : tab === 'PENDING_INVITE' ? 'Uninvite' : 'Decline'

  /** Removes active members, uninvites pending invites, or declines pending join requests. */
  const remove = (userIds: number[]) => {
    if (userIds.length === 0) return
    const options = {
      onSuccess: () => {
        toast.success(`Removed ${plural(userIds.length, 'member')}`)
        settle(userIds)
      },
      onError: () => toast.error('Failed to remove members'),
    }
    if (tab === 'ACTIVE') removeMember({ clubId, userIds }, options)
    else if (tab === 'PENDING_INVITE') uninviteMembers({ clubId, userIds }, options)
    else declineRequests({ clubId, userIds }, options)
  }

  /** Approves pending join requests; each player becomes an active member. */
  const approve = (userIds: number[]) => {
    if (userIds.length === 0) return
    approveMembers(
      { clubId, ids: userIds },
      {
        onSuccess: () => {
          toast.success(`Approved ${plural(userIds.length, 'request')}`)
          settle(userIds)
        },
        onError: () => toast.error('Failed to approve requests'),
      }
    )
  }

  return {
    remove,
    approve,
    removalLabel,
    isApproving,
    isBusy: isRemoving || isUninviting || isDeclining || isApproving,
  }
}
